import { diffLines, diffWordsWithSpace } from "diff";
import type { DiffRange } from "./CodeEditor";

export type StepFileData = { path: string; content: string };

type CommitFile = {
  path: string;
  blob: { content: string } | null | undefined;
};

export function selectStepFiles(
  commitFiles: readonly CommitFile[],
  stepName: string
): StepFileData[] {
  const prefix = `${stepName}/`;
  return commitFiles
    .filter((f) => f.path.startsWith(prefix))
    .map((f) => ({
      path: f.path.slice(prefix.length),
      content: f.blob?.content ?? "",
    }));
}

export type FileKind = "unchanged" | "modified" | "added" | "removed";

export type MergedFile = {
  path: string;
  currentContent: string | null;
  previousContent: string | null;
  kind: FileKind;
};

export type LineOrigin = {
  side: "old" | "new";
  kind: "added" | "removed" | "unchanged";
  sourceLine: number;
};

export type DiffBuild = {
  doc: string;
  diffRanges: DiffRange[];
  lineOrigins: LineOrigin[];
};

export function mergeFiles(
  current: readonly StepFileData[],
  previous: readonly StepFileData[] | undefined
): MergedFile[] {
  const currentByPath = new Map(current.map((f) => [f.path, f.content]));
  const previousByPath = new Map(
    (previous ?? []).map((f) => [f.path, f.content])
  );
  const paths = new Set([...currentByPath.keys(), ...previousByPath.keys()]);
  const result: MergedFile[] = [];
  for (const path of paths) {
    const currentContent = currentByPath.get(path) ?? null;
    const previousContent = previousByPath.get(path) ?? null;
    let kind: FileKind;
    if (currentContent == null) {
      kind = "removed";
    } else if (previousContent == null) {
      kind = previous ? "added" : "unchanged";
    } else if (currentContent === previousContent) {
      kind = "unchanged";
    } else {
      kind = "modified";
    }
    result.push({ path, currentContent, previousContent, kind });
  }
  return result;
}

export function buildDiff(oldContent: string, newContent: string): DiffBuild {
  const parts = diffLines(oldContent, newContent);
  const docParts: string[] = [];
  const diffRanges: DiffRange[] = [];
  const lineOrigins: LineOrigin[] = [];
  type Emitted = {
    kind: "added" | "removed" | "unchanged";
    value: string;
    startOffset: number;
  };
  const emitted: Emitted[] = [];
  let offset = 0;
  let oldLine = 1;
  let newLine = 1;

  for (const part of parts) {
    const value = part.value;
    if (value.length === 0) continue;
    const endsWithNewline = value.endsWith("\n");
    const linesInPart = endsWithNewline
      ? value.slice(0, -1).split("\n")
      : value.split("\n");
    const kind = part.added ? "added" : part.removed ? "removed" : "unchanged";
    const side: "old" | "new" = kind === "removed" ? "old" : "new";

    for (let i = 0; i < linesInPart.length; i++) {
      let sourceLine: number;
      if (kind === "removed") {
        sourceLine = oldLine++;
      } else if (kind === "added") {
        sourceLine = newLine++;
      } else {
        sourceLine = newLine;
        oldLine++;
        newLine++;
      }
      lineOrigins.push({ side, kind, sourceLine });
    }

    if (kind === "added" || kind === "removed") {
      diffRanges.push({ kind, from: offset, to: offset + value.length });
    }
    emitted.push({ kind, value, startOffset: offset });
    docParts.push(value);
    offset += value.length;
  }

  for (let i = 0; i < emitted.length - 1; i++) {
    const a = emitted[i]!;
    const b = emitted[i + 1]!;
    const isPair =
      (a.kind === "removed" && b.kind === "added") ||
      (a.kind === "added" && b.kind === "removed");
    if (!isPair) continue;
    const removedChunk = a.kind === "removed" ? a : b;
    const addedChunk = a.kind === "added" ? a : b;
    const wordParts = diffWordsWithSpace(removedChunk.value, addedChunk.value);
    let removedCursor = removedChunk.startOffset;
    let addedCursor = addedChunk.startOffset;
    for (const wp of wordParts) {
      const len = wp.value.length;
      if (wp.added) {
        diffRanges.push({
          kind: "added-word",
          from: addedCursor,
          to: addedCursor + len,
        });
        addedCursor += len;
      } else if (wp.removed) {
        diffRanges.push({
          kind: "removed-word",
          from: removedCursor,
          to: removedCursor + len,
        });
        removedCursor += len;
      } else {
        removedCursor += len;
        addedCursor += len;
      }
    }
    i++;
  }

  return { doc: docParts.join(""), diffRanges, lineOrigins };
}
